// Helper functions extracted from Customise component to reduce component size and
// improve testability / reuse.
// Each function is intentionally pure (no React state access) and should not touch the DOM.

import SelectedProduct from '../entities/SelectedProduct';
import { getShopCurrencyCode } from './currency';

/**
 * Derive the default variant & id given product, skintone and active section.
 * Mirrors previous getDefaultValue() logic.
 */
export function deriveDefaultVariant({ product, skintone, activeSection }) {
    if (!product || !product.variants) return { id: '', attr: null };

    const variants = product.variants.nodes ? product.variants.nodes : product.variants.edges.map((edge) => edge.node);

    if (!variants.length) return { id: '', attr: null };

    // Prefer available variants
    const availableVariants = variants.filter((v) => v?.available || v?.availableForSale);
    let attr = availableVariants[0] || variants[0];
    if ((activeSection === 'heads' || activeSection === 'legs' || activeSection === 'arms') && skintone) {
        const lowerSkin = skintone.toLowerCase();
        // Search within available variants first
        const pool = availableVariants.length ? availableVariants : variants;
        const match = pool.find((v) => v.title && v.title.toLowerCase().includes(lowerSkin));
        if (match) attr = match;
    }

    const id = attr?.id?.split('/').pop() || '';
    return { id, attr };
}

/**
 * Wrapper for SelectedProduct pricing logic (previous getPrice method).
 */
export function calcProductPrice(product, attributes, addons, productFormJsonValues) {
    return new SelectedProduct(product, null, attributes, addons, null, null, productFormJsonValues).getPrice();
}

/**
 * Compute overlay image (previous getOverLayImage). Not currently used by the component
 * but retained for potential external use.
 */
export function computeOverlayImage(product, attributes) {
    const overLayImage = new SelectedProduct(product, null, attributes, null).getOverLayImage();
    if (overLayImage) return overLayImage;
    return product?.images && product.images[0] && product.images[0].src ? product.images[0].src : '';
}

/**
 * Determine whether product has the 'overlay' tag.
 */
export function hasOverlayTag(product) {
    if (!product || !Array.isArray(product.tags)) return false;
    return product.tags.some((t) => t.replace(/\s/g, '') === 'overlay');
}

/**
 * Compute torso back image selection logic (extracted from setBackImage).
 */
export function computeTorsoBackImage(product, attributes, skintone) {
    if (!product?.images?.edges) return '';
    const attr = attributes?.title?.toLowerCase().replaceAll(' ', '');
    let torsoBackImage = '';
    let torsoBackImageSkintone = '';
    product.images.edges.forEach((image) => {
        const url = image?.node?.url?.toLowerCase?.() || '';
        if (!url) return;
        if (attr && attr !== 'defaulttitle') {
            const skinFind = `${attr}-artwork-back---${skintone}`.toLowerCase();
            if (url.includes(skinFind)) {
                torsoBackImageSkintone = image.node.url;
            } else if (url.includes(`/${attr}-back`)) {
                torsoBackImage = image.node.url;
            }
        } else {
            const skinFind = `artwork-back---${skintone}`.toLowerCase();
            if (url.includes(skinFind)) {
                torsoBackImageSkintone = image.node.url;
            } else if (url.includes('-back')) {
                torsoBackImage = image.node.url;
            }
        }
    });
    return torsoBackImageSkintone || torsoBackImage;
}

/**
 * Compute torso overlay related images (core logic from setOverlayImage) without DOM side effects.
 */
export function computeTorsoOverlayImages(product, attributes, skintone) {
    if (!product?.images?.edges) return { handImage: '', torsoOverlayImage: '' };

    const handImages = {
        dark: 'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/DarkHands-ForTorsoOverlay.png?v=1721912777',
        light: 'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/LightHands-ForTorsoOverlay.png?v=1721912777',
        yellow: 'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/YellowHands-ForTorsoOverlay.png?v=1721912778',
        nougat: 'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/NougatHands-ForTorsoOverlay.png?v=1721912778',
        medium: 'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/MediumHands-ForTorsoOverlay.png?v=1721912778',
    };

    let activeTorso = attributes;
    if (product.variants?.nodes?.length === 1) {
        activeTorso = product.variants.nodes[0];
    } else if (product.variants?.edges?.length === 1) {
        activeTorso = product.variants.edges[0].node;
    }

    const overlayValue =
        activeTorso?.torsoOverlayValue?.value === 'Light' ? 'Colour' : activeTorso?.torsoOverlayValue?.value;
    const imageStringToFind = `${overlayValue}-Artwork---${skintone}`;
    const defaultImageStrings = `${overlayValue}-Artwork---default`;
    let torsoOverlayImage = '';
    let isExactMatch = false;
    product.images.edges.forEach((image) => {
        const url = image?.node?.url || '';
        if (!url) return;
        if (url.toLowerCase().includes(imageStringToFind.toLowerCase())) {
            torsoOverlayImage = url;
            isExactMatch = true;
        } else if (!torsoOverlayImage && url.toLowerCase().includes(defaultImageStrings.toLowerCase())) {
            torsoOverlayImage = url; // fallback only if specific not already set
            isExactMatch = false;
        }
    });

    const handImage = handImages[skintone?.toLowerCase?.()] || '';
    return { handImage, torsoOverlayImage, isExactMatch };
}

// Unused image helpers retained for potential future use (previously getProductImages / getSimpleImageArray)
export function toSimpleImageArray(edges) {
    if (!Array.isArray(edges)) return [];
    return edges.map((edge) => [edge?.node?.url, '']).filter((pair) => pair[0]);
}

// Reverse head image fallbacks by skintone
export const REVERSE_HEAD_IMAGE_MAP = {
    dark: 'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/BlankDarkFlesh.png?v=1758015685',
    light: 'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/BlankLightFlesh.png?v=1758015685',
    yellow: 'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/BlankYellow.png?v=1758015685',
    nougat: 'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/BlankNougat.png?v=1758015685',
    medium: 'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/BlankMediumFlesh.png?v=1758015685',
};

export function resolveReverseHeadImage(activeSection, attributes, skintone) {
    if (activeSection !== 'heads') {
        if (activeSection === 'bodies' || activeSection === 'legs' || activeSection === 'hatsAndHair')
            return attributes?.reverseImage || null;
        return null;
    }

    const skinKey = skintone ? skintone.toLowerCase() : 'yellow';
    if (attributes?.reverseImage) {
        return attributes.reverseImage;
    }

    return { reference: { image: { url: REVERSE_HEAD_IMAGE_MAP[skinKey] } } };
}

export function computeProductPrice(product, selectedVariant) {
    const shopCurrency = getShopCurrencyCode();
    if (selectedVariant?.price?.amount) {
        return { amount: selectedVariant.price.amount, currencyCode: selectedVariant.price.currencyCode || shopCurrency };
    }

    const firstNode = product?.variants?.nodes?.[0];
    if (firstNode?.price?.amount) {
        return { amount: firstNode.price.amount, currencyCode: firstNode.price.currencyCode || shopCurrency };
    }

    const firstEdge = product?.variants?.edges?.[0]?.node;
    if (firstEdge?.price?.amount) {
        return { amount: firstEdge.price.amount, currencyCode: firstEdge.price.currencyCode || shopCurrency };
    }

    return '';
}

export function computeInStock(product) {
    if (product?.availableForSale) {
        return true;
    }

    const variantNodes = product?.variants?.nodes || product?.variants?.edges?.map((e) => e.node) || [];

    return variantNodes.some((v) => v?.available);
}
